import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  rmnProposalsEndpoint,
  rmnProposalEndpoint,
  proposalPricingPreviewEndpoint,
  proposalExamplesEndpoint,
} from '../../api/endpoints'
import { MainContainer } from '../../components/ContentWrapper'
import { ListPageHeader } from '../../components/ListPageHeader/ListPageHeader'
import { ConfirmationDialog } from '../../components/ConfirmationDialog/ConfirmationDialog'
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable'
import { useCommonParams } from '../../hooks/useCommonParams'
import { useResourceList } from '../../hooks/useResourceList'
import { IconTrash, IconPlus, IconDownload, IconPencil, IconEye } from '../../utils/icons'
import { hideAllPricing, initialsFromName, isInternal } from '../../utils'
import {
  downloadProposalPdf,
  pdfInputFromDetail,
} from '../../components/ProposalFlow/pdf/downloadProposalPdf'
import {
  ProposalDetail,
  ProposalExamplesResponse,
  ProposalPricingPreviewResponse,
} from '../../components/ProposalFlow/types'
import { useUser } from '../../hooks/useUser'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { RmnConvertProposalModal } from './RmnConvertProposalModal'
import { ProposalFlowModal } from '../../components/ProposalFlow/ProposalFlowModal'
import { StrategyFilters } from './StrategyFilters'

// RMN white-label proposals list — GET /rmn/proposals (network-scoped,
// status='proposal', deactivated:false). Row actions: open the share page,
// download the strategy PDF (same render as the share page, built from
// GET /rmn/proposals/:id plus the pricing preview + examples fetches),
// edit in place (the wizard in a dialog, PATCH /rmn/proposals/:id),
// convert to a live order (same one-step flow as the internal side, brand
// fixed to the proposal's in-network brand), and delete (soft-delete via
// the orders `deactivated` flag). All actions are available to every
// rmn-authorized user — internal with access and brand roles alike.
//
// Creating a strategy opens the wizard page (opportunities/strategies/new,
// prefixed inside a brand-portal workspace), which PDP-audit CTAs also
// deep-link into.

const PAGE_SIZE = 25

export interface RmnProposalRow {
  id: number
  name: string | null
  tier: string | null
  created_at: string
  pricing_total: number | null
  share_token: string | null
  // Drawn from the analysis the strategy started from; null if blank.
  description?: string | null
  brand: { id: number; name: string; logo?: string | null; rmn?: boolean } | null
  objective: { id: number; name: string } | null
  secondary_objective: { id: number; name: string } | null
  product_category: { id: number; name: string } | null
  products: Array<{ desireid: number; title: string; image?: string | null }>
  project_count: number
}

const formatDate = (iso: string) => (iso ? dayjs(iso).format('MMM D, YYYY') : '—')
const formatDateTime = (iso: string) => (iso ? dayjs(iso).format('MMM D, YYYY h:mm A') : '')

// Long text cells clamp to two lines (full text on hover) so every column
// fits without the table scrolling sideways.
const CLAMP = 'line-clamp-2 break-words'

const formatTotal = (total: number | null) =>
  total === null || total === undefined
    ? '—'
    : `$${Number(total).toLocaleString('en-US', { maximumFractionDigits: 0 })}`

export default function RmnProposalsPage() {
  const navigate = useNavigate()
  const { user, role } = useUser()
  const { isNetwork: isRmn, basePath } = useNetworkScope()
  // RMN pricing hide (TDC-1591): an rmn:true network's strategies show no
  // Total to anyone — internal staff in its workspace included. The
  // resolved brand's rmn flag is the signal, not the route (TDC-1499 opened
  // the /rmn/* API to every brand); outside a workspace the viewer
  // fallback applies.
  const pricingHidden = hideAllPricing(role, user, isRmn ? { brand: { rmn: true } } : null)
  const {
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    page,
    setPage,
    getFilter,
    setFilter,
  } = useCommonParams()
  const [searchInput, setSearchInput] = useState(searchText)
  const [count, setCount] = useState(0)
  const [pendingDelete, setPendingDelete] = useState<RmnProposalRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [converting, setConverting] = useState<RmnProposalRow | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [editing, setEditing] = useState<ProposalDetail | null>(null)
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null)

  useEffect(() => {
    if (searchInput === searchText) return
    const t = setTimeout(() => setSearchText(searchInput), 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  useEffect(() => {
    if (searchText !== searchInput) setSearchInput(searchText)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText])

  const productCategoryFilter = getFilter('product_category_id')
  const rmnCategoryFilter = getFilter('rmn_category_id')
  const brandFilter = getFilter('supplier_brand_id')
  const desireidFilter = getFilter('desireid')

  const list = useResourceList<RmnProposalRow>({
    endpoint: rmnProposalsEndpoint,
    pageSize: PAGE_SIZE,
    errorLabel: 'Failed to load proposals',
    searchText,
    searchParams,
    setSearchParams,
    page,
    setPage,
    onCountChange: setCount,
    extraParams: {
      product_category_id: productCategoryFilter || undefined,
      rmn_category_id: rmnCategoryFilter || undefined,
      supplier_brand_id: brandFilter || undefined,
      desireid: desireidFilter || undefined,
    },
  })

  // Brand-facing terminology is "Strategy"; the data model (and the server's
  // "Proposal - {objective}" order-name template) stays "proposal", so swap
  // the prefix for display only.
  const proposalLabel = (p: RmnProposalRow) =>
    p.name ? p.name.replace(/^Proposal - /, 'Strategy - ') : `Strategy #${p.id}`

  const handleDelete = async () => {
    if (!pendingDelete) return
    const label = proposalLabel(pendingDelete)
    setDeleting(true)
    try {
      await axiosInstant.delete(rmnProposalEndpoint(pendingDelete.id))
      toast.success(`Deleted "${label}"`)
      setPendingDelete(null)
      await list.initLoad(1)
    } catch {
      toast.error(`Couldn't delete "${label}". Please try again.`)
    } finally {
      setDeleting(false)
    }
  }

  // Mirrors ProposalViewPage's download: the persisted detail drives the
  // strategy pages, the pricing preview fills the good/better/best page and
  // the examples fill the videos/experts pages. Preview + examples are
  // non-fatal ("Custom quote" / omitted sections on failure).
  const handleDownloadPdf = async (row: RmnProposalRow) => {
    if (downloadingId !== null) return
    const label = proposalLabel(row)
    setDownloadingId(row.id)
    try {
      const { data: detail } = await axiosInstant.get<ProposalDetail>(
        rmnProposalEndpoint(row.id),
      )
      const [preview, examples] = await Promise.all([
        detail.objective && detail.channel_ids.length > 0
          ? axiosInstant
              .post<ProposalPricingPreviewResponse>(proposalPricingPreviewEndpoint, {
                objective_id: detail.objective.id,
                secondary_objective_id: detail.secondary_objective
                  ? detail.secondary_objective.id
                  : undefined,
                channel_ids: detail.channel_ids,
              })
              .then((r) => r.data)
              .catch(() => null)
          : Promise.resolve(null),
        detail.product_category
          ? axiosInstant
              .get<ProposalExamplesResponse>(
                proposalExamplesEndpoint(
                  detail.product_category.id,
                  detail.content_type_units.map((u) => u.content_type_id),
                ),
              )
              .then((r) => r.data)
              .catch(() => null)
          : Promise.resolve(null),
      ])
      // Attached-shortlist videos ride along as extra examples.
      const shortlistVideos = (detail.video_shortlist && detail.video_shortlist.videos) || []
      let mergedExamples: ProposalExamplesResponse | null = examples
      if (examples || shortlistVideos.length > 0) {
        const videos = [...((examples && examples.videos) || [])]
        shortlistVideos.forEach((v) => {
          if (!videos.some((x) => x.id === v.id)) videos.push(v)
        })
        mergedExamples = {
          product_category_id: examples ? examples.product_category_id : 0,
          videos,
          experts: (examples && examples.experts) || [],
        }
      }
      // The detail's brand.rmn flag is authoritative; inside an rmn
      // workspace the page-level flag already hides pricing.
      const rowPricingHidden = pricingHidden || hideAllPricing(role, user, detail)
      await downloadProposalPdf(
        pdfInputFromDetail(detail, {
          internal: isInternal(role),
          showPricing: !rowPricingHidden,
          showPriceTeaser: !rowPricingHidden,
          preview,
          shareUrl: row.share_token
            ? `${window.location.origin}/proposal/view/${row.share_token}`
            : null,
          examples: mergedExamples,
        }),
      )
    } catch {
      toast.error(`Couldn't build the PDF for "${label}". Please try again.`)
    } finally {
      setDownloadingId(null)
    }
  }

  // Edit reuses the wizard in edit mode (same as the internal sales detail
  // page) against the rmnContext-scoped PATCH; the row payload is a summary,
  // so the full detail is fetched first.
  const handleEdit = async (row: RmnProposalRow) => {
    if (editLoadingId !== null) return
    setEditLoadingId(row.id)
    try {
      const { data } = await axiosInstant.get<ProposalDetail>(rmnProposalEndpoint(row.id))
      setEditing(data)
    } catch {
      toast.error(`Couldn't open "${proposalLabel(row)}" for editing. Please try again.`)
    } finally {
      setEditLoadingId(null)
    }
  }

  const columns: DataTableColumn<RmnProposalRow>[] = [
    {
      key: 'name',
      header: 'Strategy',
      render: (p) => (
        <span className={`font-semibold text-text-1 max-w-[200px] ${CLAMP}`} title={proposalLabel(p)}>
          {proposalLabel(p)}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (p) =>
        p.description ? (
          <span className={`text-text-2 text-[12px] leading-[1.45] min-w-[220px] max-w-[340px] ${CLAMP}`} title={p.description}>
            {p.description}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'brand',
      header: 'Brand',
      render: (p) =>
        p.brand ? (
          <div className="flex items-center gap-[10px]">
            <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
              {p.brand.logo ? (
                <img src={p.brand.logo} alt="" className="w-full h-full object-cover" />
              ) : (
                initialsFromName(p.brand.name)
              )}
            </div>
            <span className={`max-w-[120px] ${CLAMP}`}>{p.brand.name}</span>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'objective',
      header: 'Objective',
      render: (p) => (
        <span className={`text-text-2 max-w-[180px] ${CLAMP}`}>
          {p.objective?.name || '—'}
          {p.secondary_objective ? ` + ${p.secondary_objective.name}` : ''}
        </span>
      ),
    },
    {
      key: 'products',
      header: 'Products',
      render: (p) => {
        const products = p.products || []
        if (products.length === 0) return <span className="text-muted">—</span>
        const shown = products.slice(0, 2)
        const extra = products.length - shown.length
        return (
          <span
            className={`text-text-2 max-w-[180px] ${CLAMP}`}
            title={products.map((d) => d.title).join(', ')}
          >
            {shown.map((d) => d.title).join(', ')}
            {extra > 0 ? ` +${extra}` : ''}
          </span>
        )
      },
    },
    {
      key: 'projects',
      header: 'Projects',
      render: (p) => <span className="text-text-2">{p.project_count}</span>,
    },
    ...(pricingHidden
      ? []
      : [
          {
            key: 'total',
            header: 'Total',
            render: (p: RmnProposalRow) => (
              <span className="font-semibold">{formatTotal(p.pricing_total)}</span>
            ),
          },
        ]),
    {
      key: 'created',
      header: 'Created',
      render: (p) => <span title={formatDateTime(p.created_at)}>{formatDate(p.created_at)}</span>,
      className: 'text-muted text-[12px] whitespace-nowrap',
    },
    {
      key: 'actions',
      header: '',
      render: (p) => (
        <div className="flex items-center gap-0.5 justify-end whitespace-nowrap">
          <button
            type="button"
            title={editLoadingId === p.id ? 'Opening…' : 'Edit strategy'}
            aria-label="Edit strategy"
            disabled={editLoadingId !== null}
            onClick={() => handleEdit(p)}
            className={`w-8 h-8 rounded-md text-muted flex items-center justify-center bg-transparent border-0 hover:text-text-1 hover:bg-surface-2 ${
              editLoadingId === p.id ? 'animate-pulse cursor-wait' : 'cursor-pointer'
            } disabled:opacity-60`}
          >
            <IconPencil size={16} stroke="currentColor" />
          </button>
          <button
            type="button"
            title={downloadingId === p.id ? 'Preparing PDF…' : 'Download PDF'}
            aria-label="Download PDF"
            disabled={downloadingId !== null}
            onClick={() => handleDownloadPdf(p)}
            className={`w-8 h-8 rounded-md text-muted flex items-center justify-center bg-transparent border-0 hover:text-text-1 hover:bg-surface-2 ${
              downloadingId === p.id ? 'animate-pulse cursor-wait' : 'cursor-pointer'
            } disabled:opacity-60`}
          >
            <IconDownload width={16} height={16} stroke="currentColor" />
          </button>
          {p.share_token && (
            <button
              type="button"
              title="Open the shareable strategy page"
              aria-label="View strategy"
              onClick={() => window.open(`/proposal/view/${p.share_token}`, '_blank')}
              className="w-8 h-8 rounded-md text-muted flex items-center justify-center bg-transparent border-0 cursor-pointer hover:text-text-1 hover:bg-surface-2"
            >
              <IconEye width={16} height={16} stroke="currentColor" />
            </button>
          )}
          <button
            type="button"
            title="Convert this strategy into a live order"
            onClick={() => setConverting(p)}
            className="text-[12px] font-semibold text-white bg-accent rounded-md px-2.5 py-1.5 border-0 cursor-pointer hover:bg-[#8A691F] whitespace-nowrap"
          >
            Convert
          </button>
          <button
            type="button"
            title="Delete"
            onClick={() => setPendingDelete(p)}
            className="w-8 h-8 rounded-md text-muted flex items-center justify-center cursor-pointer hover:text-down hover:bg-down-bg"
          >
            <IconTrash width={16} height={16} stroke="currentColor" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <MainContainer>
      <ListPageHeader
        title="Strategies"
        subtitle={
          count > 0 ? `Draft strategies for your network · ${count} total` : 'Draft strategies for your network'
        }
        actionLabel="Create New Strategy"
        onActionClick={() =>
          navigate(`${basePath}/opportunities/strategies/new`)
        }
        actionIcon={<IconPlus width={16} height={16} stroke="#fff" />}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchInputId="search-rmn-proposals"
        searchPlaceholder="Search by name…"
        searchRightContent={
          <StrategyFilters
            productCategoryId={productCategoryFilter}
            rmnCategoryId={rmnCategoryFilter}
            brandId={brandFilter}
            desireid={desireidFilter}
            onChange={(key, value) => setFilter(key, value)}
          />
        }
      />

      <DataTable<RmnProposalRow>
        columns={columns}
        rows={list.items}
        loading={list.loading}
        totalCount={list.totalCount}
        hasMore={list.hasMore}
        error={list.error}
        onLoadMore={list.loadMore}
        emptyMessage="No strategies yet — create one to get started"
        getRowKey={(r) => r.id}
      />

      {editing && (
        <ProposalFlowModal
          open
          onClose={() => setEditing(null)}
          rmn={{}}
          edit={{ proposal: editing, endpoint: rmnProposalEndpoint(editing.id) }}
          onSaved={async () => {
            setEditing(null)
            toast.success('Strategy updated')
            await list.initLoad(1)
          }}
          onCancelEdit={() => setEditing(null)}
        />
      )}

      {converting && (
        <RmnConvertProposalModal
          proposal={converting}
          onClose={() => setConverting(null)}
          onConverted={async () => {
            setConverting(null)
            await list.initLoad(1)
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmationDialog
          open
          title="Delete Strategy"
          description={`Delete "${proposalLabel(pendingDelete)}"? This cannot be undone.`}
          confirmLabel="Delete"
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          disabled={deleting}
        />
      )}
    </MainContainer>
  )
}
